import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Photo } from '@capacitor/camera';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { CapacitorImageService } from '@examdojo/core/image-manipulation/storage';
import { mapToVoid } from '@examdojo/core/rxjs';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { ImageLocation } from '@examdojo/question';
import { RealtimeChangesListenEvent, RealtimeService } from '@examdojo/supabase';
import { randUuid } from '@ngneat/falso';
import { concatMap, distinctUntilChanged, from, map, merge, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { CurrentReviewHttpModel, ReviewItemQuery } from '../review-item';
import { ReviewSolutionImagesHttpService } from './review-solution-image-http.service';
import {
  REVIEW_SOLUTION_IMAGE_BUCKET_NAME,
  ReviewSolutionImageCreateModel,
  ReviewSolutionImageStoreModel,
} from './review-solution-image.model';
import { ReviewSolutionImageQuery } from './review-solution-image.query';
import { ReviewSolutionImageStore } from './review-solution-image.store';

@Injectable({ providedIn: 'root' })
export class ReviewSolutionImageService {
  constructor(
    private readonly realtimeService: RealtimeService,
    private readonly query: ReviewSolutionImageQuery,
    private readonly store: ReviewSolutionImageStore,
    private readonly httpService: ReviewSolutionImagesHttpService,
    private readonly capacitorImageService: CapacitorImageService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly reviewItemQuery: ReviewItemQuery,
  ) {
    this.listenForReviewSolutionImageUpdates().pipe(takeUntilDestroyed()).subscribe();
  }

  private readonly resetListener$ = new Subject<null>();

  fetchAll(reviewSession: CurrentReviewHttpModel['review_session']) {
    this.store.reset();

    return this.httpService.fetchAllFromCurrentSession(reviewSession).pipe(
      switchMap((images) => {
        return from(
          images.length
            ? this.httpService.fetchSignedUrls(
                images.map((image) => image.image_name).filter(isNotNullish),
                REVIEW_SOLUTION_IMAGE_BUCKET_NAME,
              )
            : of({} as Record<string, string | null>),
        ).pipe(
          this.errorHandlerService.setHttpErrorMetadata({
            entity: 'user_images',
            context: {
              reviewSession,
            },
          }),
          tap((signedUrls) => {
            const storeImages: ReviewSolutionImageStoreModel[] = images.map((image) => ({
              ...image,
              url: image.image_name ? signedUrls[image.image_name] : null,
              location: ImageLocation.Remote,
            }));

            this.store.upsertMany(storeImages);
          }),
          mapToVoid(),
        );
      }),
    );
  }

  saveSolutionImage(
    userId: string,
    reviewSession: CurrentReviewHttpModel['review_session'],
    fileOrPhoto: Photo | File,
  ) {
    const currentResponseImagesList = this.query.getAllEntities();

    const order = currentResponseImagesList.length
      ? currentResponseImagesList.reduce(
          (max, responseImage) => ((responseImage.order ?? 0) > max ? responseImage.order ?? 0 : max),
          0,
        ) + 1
      : 0;

    const image$ = 'name' in fileOrPhoto ? from(this.capacitorImageService.fileToPhoto(fileOrPhoto)) : of(fileOrPhoto);

    return image$.pipe(
      map((image) => ({ image, fileName: this.capacitorImageService.getFileName(userId, image.format) })),
      tap(({ image, fileName }) => {
        fileName = fileName.replace(/\.heic$/i, '.jpg');

        const localAttemptImageStoreModel = this.mapPhotoToLocalReviewSolutionImageStoreModel({
          url: image.dataUrl ?? '',
          imageName: fileName,
          bucketName: REVIEW_SOLUTION_IMAGE_BUCKET_NAME,
          id: randUuid(),
          order,
          userId: userId,
        });

        this.store.upsertMany([localAttemptImageStoreModel]);
      }),
      switchMap(({ image, fileName }) =>
        this.capacitorImageService.uploadImage(REVIEW_SOLUTION_IMAGE_BUCKET_NAME, image, fileName),
      ),
      switchMap((uploadResult) => {
        const createModel: ReviewSolutionImageCreateModel = {
          order,
          bucket_name: REVIEW_SOLUTION_IMAGE_BUCKET_NAME,
          image_name: uploadResult.path,
          current_review_session: reviewSession,
          user_id: userId,
        };

        return this.httpService.create(createModel).pipe(
          tap((httpModel) => {
            this.replaceLocalReviewSolutionImage({
              ...httpModel,
              location: ImageLocation.Remote,
            });
          }),
        );
      }),
    );
  }

  async addNewSolutionImage(): Promise<null | Photo> {
    return this.capacitorImageService.takePicture();
  }

  listenForReviewSolutionImageUpdates() {
    return merge(this.reviewItemQuery.selectSession().pipe(distinctUntilChanged()), this.resetListener$).pipe(
      distinctUntilChanged(),
      switchMap((reviewSession) => {
        if (!reviewSession) {
          return of([]);
        }

        return merge(
          this.realtimeService
            .listenBroadcastRealtime<ReviewSolutionImageStoreModel>(
              [{ event: RealtimeChangesListenEvent.DELETE }],
              'user_images_delete',
            )
            .pipe(
              tap((payload) => {
                if (
                  payload.event === RealtimeChangesListenEvent.DELETE &&
                  payload?.payload?.old_record?.id &&
                  this.store.hasEntity(payload.payload.old_record.id)
                ) {
                  this.store.delete(payload.payload.old_record.id);
                }
              }),
            ),
          this.realtimeService
            .listenBroadcastRealtime<ReviewSolutionImageStoreModel>(
              [{ event: RealtimeChangesListenEvent.INSERT }],
              `user_images:session:${reviewSession}`,
            )
            .pipe(
              concatMap((payload) => {
                if (
                  payload.event === RealtimeChangesListenEvent.INSERT &&
                  payload?.payload?.record?.id &&
                  !this.store.hasEntity(payload.payload.record.id)
                ) {
                  return this.mapReviewSolutionImageHttpModelToStoreModel([payload.payload.record]).pipe(
                    tap((reviewSolutionImages) => this.store.upsertMany(reviewSolutionImages)),
                  );
                }
                return of();
              }),
            ),
        );
      }),
    );
  }

  reset() {
    this.resetListener$.next(null);
    this.store.reset();
  }

  removeReviewSolutionImage(entityId: ReviewSolutionImageStoreModel['id']): Observable<void> {
    const entity = this.query.getEntity(entityId);

    if (!entity) {
      return of(undefined);
    }

    return this.httpService.delete(entity.id).pipe(
      tap(() => this.store.delete(entity.id)),
      switchMap(() => {
        assertNonNullable(entity.bucket_name, 'bucket_name');
        assertNonNullable(entity.image_name, 'image_name');
        return this.httpService.deleteQuestionAttemptResponseImageFile(entity.bucket_name, entity.image_name);
      }),
      mapToVoid(),
    );
  }

  private replaceLocalReviewSolutionImage(model: ReviewSolutionImageStoreModel) {
    const oldEntity = this.query.getEntityByPredicate((item) => model.image_name === item.image_name);

    if (!oldEntity) {
      return;
    }

    this.store.update(oldEntity.id, (item) => ({
      ...model,
      url: item.url, // do not replace url, to avoid flicking (optimistic UI)
      location: ImageLocation.Remote,
    }));

    this.store.updateEntitiesIds(oldEntity.id, model.id);
  }

  private mapPhotoToLocalReviewSolutionImageStoreModel({
    url,
    imageName,
    bucketName,
    id,
    order,
    userId,
    currentReviewSession,
  }: {
    url: string;
    imageName: string;
    bucketName: string;
    id: string;
    order: number;
    userId: string;
    currentReviewSession?: string;
  }): ReviewSolutionImageStoreModel {
    return {
      location: ImageLocation.Local,
      url,
      image_name: imageName,
      bucket_name: bucketName,
      id,
      order,
      user_id: userId,
      current_review_session: currentReviewSession ?? null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      action_id: null,
    };
  }

  private mapReviewSolutionImageHttpModelToStoreModel(
    reviewSolutionImages: ReviewSolutionImageStoreModel[],
  ): Observable<ReviewSolutionImageStoreModel[]> {
    const reviewSolutionImagesWithNames = reviewSolutionImages.filter((item) => !!item.image_name);
    const reviewSolutionImageBucketName = reviewSolutionImagesWithNames[0]?.bucket_name;

    if (!reviewSolutionImagesWithNames.length || !reviewSolutionImageBucketName) {
      return of(
        reviewSolutionImages.map(
          (item): ReviewSolutionImageStoreModel => ({
            ...item,
            url: null,
            location: ImageLocation.Remote,
          }),
        ),
      );
    }

    const reviewSolutionImageNames = reviewSolutionImagesWithNames.map((item) => item.image_name).filter(isNotNullish);

    return from(this.httpService.fetchSignedUrls(reviewSolutionImageNames, reviewSolutionImageBucketName)).pipe(
      map((signedUrls) =>
        reviewSolutionImagesWithNames.map(
          (item): ReviewSolutionImageStoreModel => ({
            ...item,
            url: item.image_name ? signedUrls[item.image_name] : null,
            location: ImageLocation.Remote,
          }),
        ),
      ),
    );
  }
}
